import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { clearAppBadge } from "@/lib/appBadge";

// Task #324 — a 401 means the session ended (expiry, revocation, …), not just
// an explicit logout. Clear the PWA icon badge so the previous session's
// unread count never lingers on a shared device. Fire-and-forget; never let
// badge failures affect the request path.
function onAuthLost(res: Response) {
  if (res.status === 401) {
    void clearAppBadge();
    // Task #325 — tell the service worker the session is dead so its push
    // handler stops re-badging the icon if a stray push arrives before the
    // server-side gate catches up. Fire-and-forget.
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: "auth-lost" });
    } catch {
      /* never let SW messaging affect the request path */
    }
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  onAuthLost(res);
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    onAuthLost(res);
    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
